import { initializeApp } from "firebase/app";
import { doc, getDoc, getFirestore, collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { ElementType } from "./ElementType.js";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

initializeApp(firebaseConfig);
const db = getFirestore();

/**
 * Helper function to parse DD/MM/YYYY date strings into Date objects
 * @param {string} dateString - Date in DD/MM/YYYY format
 * @returns {Date} Parsed date object
 */
function parseDDMMYYYY(dateString) {
  if (!dateString) return new Date(0);
  const [day, month, year] = dateString.split('/');
  return new Date(year, month - 1, day);
}

/**
 * Element type can be [news], [events], [authors]
 * 
 */
export async function getElementDetail(elementType, id) {

  if (Number.isInteger(id) && elementType instanceof ElementType) {
    const q = query(collection(db, elementType.elementName), where("id", "==", id));

    const querySnapshot = await getDocs(q);
    var element;
    querySnapshot.forEach((doc) => {
      element = doc.data();
    });

    return element;
  } else {

    return;
  }
}

/**
 * Element type can be [news], [events]
 * 
 */
export async function getAllElementsOrderedDesc(elementType) {

  if (elementType instanceof ElementType) {
    const q = query(collection(db, elementType.elementName));

    const querySnapshot = await getDocs(q);
    var elements = [];
    querySnapshot.forEach((doc) => {
      elements.push(doc.data());
    });

    // Sort by date (DD/MM/YYYY format) in descending order
    elements.sort((a, b) => {
      const dateA = parseDDMMYYYY(a.date);
      const dateB = parseDDMMYYYY(b.date);
      return dateB - dateA;
    });

    return elements;
  } else {
    return;
  }
}

/**
 * Element type can be [news], [events]
 * 
 */
export async function getElementsForFirstPage(elementType) {

  if (elementType instanceof ElementType) {
    const q = query(collection(db, elementType.elementName));

    const querySnapshot = await getDocs(q);
    var elements = [];
    querySnapshot.forEach((doc) => {
      elements.push(doc.data());
    });

    // Sort by date (DD/MM/YYYY format) in descending order
    elements.sort((a, b) => {
      const dateA = parseDDMMYYYY(a.date);
      const dateB = parseDDMMYYYY(b.date);
      return dateB - dateA;
    });

    return elements.slice(0, 12);
  } else {
    return;
  }
}

/**
 * Element type can be [news], [events]
 * 
 */
export async function getElementsForOtherPages(elementType, elementNumber, pageSelected) {

  if (elementType instanceof ElementType) {

    let skip = parseInt(pageSelected) * 12;

    const q = query(collection(db, elementType.elementName));

    const querySnapshot = await getDocs(q);
    var elements = [];
    querySnapshot.forEach((doc) => {
      elements.push(doc.data());
    });

    // Sort by date (DD/MM/YYYY format) in descending order
    elements.sort((a, b) => {
      const dateA = parseDDMMYYYY(a.date);
      const dateB = parseDDMMYYYY(b.date);
      return dateB - dateA;
    });

    // Manually paginate the results
    return elements.slice(skip, skip + 12);
  } else {
    return;
  }
}

/**
 * Element type can be [members], [authors]
 * 
 */
export async function getElements(elementType) {
  if (elementType instanceof ElementType) {
    const q = query(collection(db, elementType.elementName));

    const querySnapshot = await getDocs(q);
    var elements = [];
    querySnapshot.forEach((doc) => {
      elements.push(doc.data());
    });

    return elements;
  } else {
    return;
  }
}

//#region SPECIAL QUERIES
/**
 * Fetch the last N events (ordered by ID descending)
 * Note: This returns events ordered by ID, not by date
 * @param {number} limitCount - Number of events to fetch (default: 6)
 * @returns {Promise<Array>} Array of event objects
 */
export async function getLastEvents(limitCount = 6) {
  const q = query(collection(db, "events"), orderBy("id", "desc"), limit(limitCount));

  const querySnapshot = await getDocs(q);
  var events = [];
  querySnapshot.forEach((doc) => {
    events.push(doc.data());
  });

  return events;
}

export async function getLastArticles() {

  const q = query(collection(db, "news"));

  const querySnapshot = await getDocs(q);
  var articles = [];
  querySnapshot.forEach((doc) => {
    articles.push(doc.data());
  });

  // Sort by date (DD/MM/YYYY format) in descending order
  articles.sort((a, b) => {
    const dateA = parseDDMMYYYY(a.date);
    const dateB = parseDDMMYYYY(b.date);
    return dateB - dateA;
  });

  return articles.slice(0, 4);
}
//#endregion


//#region utility function
export async function getAllElementsNumber(elementType) {
  const valuesDocRef = doc(db, "values", "values");
  const valuesDocSnap = await getDoc(valuesDocRef);

  switch (elementType) {
    case ElementType.ArticleType.elementName:
      return valuesDocSnap.data().news;
    case ElementType.EventType.elementName:
      return valuesDocSnap.data().events;
    case ElementType.MemberType.elementName:
      return valuesDocSnap.data().members;
    case ElementType.PhotoType.elementName:
      return valuesDocSnap.data().photos;
    default: return
  }
}

export async function getLastId(elementType) {
  switch (elementType) {
    case ElementType.ArticleType: {
      const q = query(collection(db, "news"), orderBy("id", "desc"), limit(1));

      const querySnapshot = await getDocs(q);
      var articles = [];
      querySnapshot.forEach((doc) => {
        articles.push(doc.data());
      });

      return articles[0].id;
    }

    case ElementType.EventType: {
      const q = query(collection(db, "events"), orderBy("id", "desc"), limit(1));

      const querySnapshot = await getDocs(q);
      var events = [];
      querySnapshot.forEach((doc) => {
        events.push(doc.data());
      });

      return events[0].id;
    }

    default: return
  }
}

//#endregion